'use client';

import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  APP_LANGUAGES,
  AppLanguageCode,
  applyGoogleTranslateLanguage,
  flagImageUrl,
  readActiveLanguageFromCookie,
} from '@/lib/googleTranslate';
import { isShootingSessionActive } from '@/lib/shootingGuideResume';

type Props = {
  variant: 'desktop' | 'mobile';
};

function FlagIcon({ flagIso }: { flagIso: string }) {
  return (
    <span
      role="img"
      aria-hidden
      className="flag-icon inline-block rounded-sm shadow-sm shrink-0"
      style={{
        width: 22,
        height: 16,
        backgroundImage: `url(${flagImageUrl(flagIso, 40)})`,
      }}
    />
  );
}

const SWITCHER_ROOT_CLASS = 'language-switcher notranslate skiptranslate';

export default function LanguageSwitcher({ variant }: Props) {
  const [active, setActive] = useState<AppLanguageCode>('fr');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActive(readActiveLanguageFromCookie());
  }, []);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  const pick = (code: AppLanguageCode) => {
    if (isShootingSessionActive()) {
      toast.error(
        'Terminez ou quittez la scène en cours avant de changer de langue.',
      );
      setOpen(false);
      return;
    }
    setActive(code);
    setOpen(false);
    applyGoogleTranslateLanguage(code);
  };

  const current = APP_LANGUAGES.find((l) => l.code === active) ?? APP_LANGUAGES[0];

  if (variant === 'desktop') {
    return (
      <div
        className={`${SWITCHER_ROOT_CLASS} flex items-center gap-0.5 px-1 py-0.5 rounded-full`}
        style={{ border: '1px solid #E8E4DF', backgroundColor: '#FFFFFF' }}
        role="group"
        aria-label="Traduction automatique"
        translate="no"
      >
        {APP_LANGUAGES.map((lang) => {
          const isActive = lang.code === active;
          return (
            <button
              key={lang.code}
              type="button"
              title={lang.label}
              onClick={() => pick(lang.code)}
              className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden transition-all"
              style={{
                backgroundColor: isActive ? 'rgba(212,31,76,0.12)' : 'transparent',
                boxShadow: isActive ? '0 0 0 2px rgba(212,31,76,0.4)' : 'none',
              }}
              aria-label={lang.label}
              aria-pressed={isActive}
            >
              <FlagIcon flagIso={lang.flagIso} />
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div ref={rootRef} className={`${SWITCHER_ROOT_CLASS} relative`} translate="no">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-xs font-bold transition-all min-w-[3.5rem] justify-center"
        style={{
          fontFamily: "'Acme', 'Work Sans', Helvetica, sans-serif",
          color: '#4A4560',
          border: '1px solid #E8E4DF',
          backgroundColor: open ? '#F0EDE8' : '#FFFFFF',
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Langue : ${current.label}`}
      >
        <FlagIcon flagIso={current.flagIso} />
        <ChevronDown
          size={14}
          style={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 top-full mt-1 min-w-[4.5rem] rounded-xl py-1 z-[70] shadow-lg"
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E8E4DF',
          }}
        >
          {APP_LANGUAGES.map((lang) => (
            <li key={lang.code} role="option" aria-selected={lang.code === active}>
              <button
                type="button"
                onClick={() => pick(lang.code)}
                className="w-full px-3 py-2.5 flex items-center justify-center gap-2 text-xs font-bold transition-colors hover:bg-[#F8F5F2]"
                style={{
                  fontFamily: "'Acme', 'Work Sans', Helvetica, sans-serif",
                  color: lang.code === active ? '#D41F4C' : '#4A4560',
                }}
              >
                <FlagIcon flagIso={lang.flagIso} />
                <span>{lang.mobileCode}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
